import { Component, Inject, inject, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IVMailboxWidget, IWidgetHeaderOptions } from '../interfaces/vmailbox-widget.interface';
import { MatSelectChange } from '@angular/material/select';
import { ModelService, PluginInjectorWidget, PluginInjectorService } from 'core-client/api';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AsteriskVoicemail } from '../../shared/services/db/asteriks-entities.interface';
import { VMailboxWidgetService } from '../services/vmailbox-widget.service';

@Component({
	selector: 'lib-vmailbox-widget-settings',
	imports: [SharedModule],
	templateUrl: './vmailbox-widget-settings.component.html',
	styleUrl: './vmailbox-widget-settings.component.scss',
})
export class VMailboxWidgetSettingsComponent implements OnInit {

	// Services
	protected readonly translate: TranslateService = inject(TranslateService);
	protected readonly dialogRef: MatDialogRef<VMailboxWidgetSettingsComponent, any> =
		inject(MatDialogRef<VMailboxWidgetSettingsComponent>);
	protected readonly modelService: ModelService = inject(ModelService);
	private readonly snackBar = inject(MatSnackBar);
	protected readonly pluginInjectorService: PluginInjectorService = inject(PluginInjectorService);
	private readonly vmailboxService = inject(VMailboxWidgetService);

	// Variables
	protected vmailboxWidget: IVMailboxWidget;
	protected selectedWidgetHeaderOption: string = 'alwaysDisplayed';
	protected widgetTitle: string;
	protected showWidgetTitle: boolean = false;
	protected widgetId: string;

	// Mailbox selection
	protected availableMailboxes: AsteriskVoicemail[] = [];
	protected selectedMailboxNr: string | undefined;
	protected isLoadingMailboxes = false;
	protected mailboxLoadError = false;

	constructor(@Inject(MAT_DIALOG_DATA) protected readonly data: PluginInjectorWidget.IWidget) {
		this.vmailboxWidget = data.appPluginWidget;
		this.widgetId = data.appPluginWidget._id;
		this.widgetTitle = data.appPluginWidget.name;
		this.showWidgetTitle = data.appPluginWidget.settings.showWidgetTitle;
		const widgetHeaderOption = data.appPluginWidget.settings.widgetHeader;
		const widgetHeaderOptions = Object.keys(widgetHeaderOption) as IWidgetHeaderOptions[];
		this.selectedWidgetHeaderOption = widgetHeaderOptions.filter(
			(key: IWidgetHeaderOptions) => widgetHeaderOption[key],
		)[0];
		// Restore previously saved mailbox selection
		this.selectedMailboxNr = this.vmailboxWidget.settings.mailboxNr;
	}

	ngOnInit(): void {
		this.loadAvailableMailboxes();
	}

	private loadAvailableMailboxes(): void {
		this.isLoadingMailboxes = true;
		this.mailboxLoadError = false;

		this.vmailboxService.getMailboxes().subscribe({
			next: (boxes: AsteriskVoicemail[]) => {
				this.availableMailboxes = boxes;
				this.isLoadingMailboxes = false;
			},
			error: () => {
				// Fall back to JWT-derived nr list so the user can pick a mailbox
				const user = this.modelService._user.getUser();
				const nrs: string[] = user?.astimaxAccount?.voicemail ?? [];
				this.availableMailboxes = nrs.map((nr) => ({ id: 0, nr, name: '' } as AsteriskVoicemail));
				this.mailboxLoadError = true;
				this.isLoadingMailboxes = false;
			},
		});
	}

	protected selectHeaderOption($event: MatSelectChange): void {
		const headerOptions: Record<string, { alwaysDisplayed: boolean; onHoverDisplayed: boolean; alwaysOff: boolean; transparent: boolean }> = {
			alwaysDisplayed:  { alwaysDisplayed: true,  onHoverDisplayed: false, alwaysOff: false, transparent: false },
			onHoverDisplayed: { alwaysDisplayed: false, onHoverDisplayed: true,  alwaysOff: false, transparent: false },
			alwaysOff:        { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: true,  transparent: false },
			transparent:      { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: false, transparent: true  },
		};
		if ($event.value in headerOptions) {
			this.vmailboxWidget.settings.widgetHeader = headerOptions[$event.value];
		}
	}

	protected getText(key: unknown): string {
		const translations: Record<string, string> = {
			alwaysDisplayed:  'ASTIMAX.VMAILBOX.WIDGET.WIDGET_HEADER.ALWAYS_DISPLAYED',
			onHoverDisplayed: 'ASTIMAX.VMAILBOX.WIDGET.WIDGET_HEADER.ON_HOVER_DISPLAYED',
			alwaysOff:        'ASTIMAX.VMAILBOX.WIDGET.WIDGET_HEADER.ALWAYS_OFF',
			transparent:      'ASTIMAX.VMAILBOX.WIDGET.WIDGET_HEADER.TRANSPARENT',
		};
		return this.translate.instant(translations[key as string] || 'unknown');
	}

	protected mailboxLabel(mailbox: AsteriskVoicemail): string {
		return mailbox.name ? `${mailbox.nr} - ${mailbox.name}` : mailbox.nr;
	}

	async onSubmit(): Promise<void> {
		if (!this.vmailboxWidget) return;

		this.vmailboxWidget.name = this.widgetTitle;
		this.vmailboxWidget.settings.showWidgetTitle = this.showWidgetTitle;
		this.vmailboxWidget.settings.mailboxNr = this.selectedMailboxNr ?? undefined;

		await this.modelService._appPluginWidget.upsertOne(this.vmailboxWidget);

		const widget = this.pluginInjectorService.widgetService.getWidgetItem(this.vmailboxWidget._id);
		if (widget) {
			widget.name = this.vmailboxWidget.name;
		}

		this.vmailboxService.setMailboxNr(this.widgetId, this.selectedMailboxNr);

		this.snackBar.open(
			this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.SETTINGS_CHANGES_UPDATED_SUCCESSFULLY'),
			'Close',
			{ duration: 3000 },
		);

		this.dialogRef.close();
	}
}
